import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useCallback, forwardRef, useState, useEffect } from 'react';
import { Button, ButtonVariant } from '@sas/nova-commons/components/Button';
import { MenuButton } from '@sas/nova-commons/components/MenuButton';
import { scrollHorizontal, scrollVertical } from './ScrollUtil';
import { StackButton, dragType } from './StackButton';
import { HorizontalPosition, VerticalPosition, ButtonType } from '@sas-dvr/ltjs-commons';
import { useDesignTokens } from '@sas/nova-commons/theme';
// eslint-disable-next-line no-restricted-imports
import { TabDragPreview } from '@sas/nova-commons/components/TabBar/__internal__/dnd/TabDragPreview';
import { createUseHtmlId } from '@sas/nova-commons/hooks/useHtmlId';
import { useIsRTL } from '@sas/nova-commons/locale';
import { useResizeObserverRef } from '@sas/nova-commons/hooks/useResizeObserver';
import { DisclosurePrevious, DisclosureNext, DisclosureCollapse, DisclosureExpand, TabOverflow, } from '@sas/react-svg';
const useStackNavigationDndPrefix = createUseHtmlId('StackNavigation-dndPrefix');
export const StackNavigation = forwardRef((props, ref) => {
    const textColor = useDesignTokens().stateStandardContentNova.normal.fg;
    const [isFocusActive, setIsFocusActive] = useState(false);
    const dndPrefix = useStackNavigationDndPrefix();
    const { buttonType, items } = props;
    const nextEnabled = props.selectedIndex < items.length - 1;
    const previousEnabled = props.selectedIndex !== 0;
    let flexDirection = undefined;
    let previousIcon;
    let nextIcon;
    let tabLocation;
    let textAlign;
    const outerDivStyle = {
        padding: '.5rem',
        display: 'inline-flex',
        flexShrink: 0,
        boxSizing: 'border-box',
    };
    if (props.direction === 'horizontal') {
        flexDirection = 'row';
        outerDivStyle.flexDirection = flexDirection;
        outerDivStyle.maxWidth = '100%';
        previousIcon = DisclosurePrevious;
        nextIcon = DisclosureNext;
        switch (props.horizontalPosition) {
            case HorizontalPosition.LEFT:
                outerDivStyle.justifyContent = 'flex-start';
                break;
            case HorizontalPosition.RIGHT:
                outerDivStyle.justifyContent = 'flex-end';
                break;
            default:
                outerDivStyle.justifyContent = 'center';
                break;
        }
        tabLocation = props.verticalPosition === VerticalPosition.TOP ? 'bottom' : 'top';
        textAlign = 'center';
    }
    else {
        flexDirection = 'column';
        outerDivStyle.flexDirection = flexDirection;
        outerDivStyle.maxHeight = '100%';
        previousIcon = DisclosureCollapse;
        nextIcon = DisclosureExpand;
        outerDivStyle.maxWidth = '25%';
        switch (props.verticalPosition) {
            case VerticalPosition.TOP:
                outerDivStyle.justifyContent = 'flex-start';
                break;
            case VerticalPosition.BOTTOM:
                outerDivStyle.justifyContent = 'flex-end';
                break;
            default:
                outerDivStyle.justifyContent = 'center';
                break;
        }
        tabLocation = props.horizontalPosition === HorizontalPosition.LEFT ? 'right' : 'left';
        textAlign = props.horizontalPosition === HorizontalPosition.LEFT ? 'right' : 'left';
    }
    const isRTL = useIsRTL();
    const onKeyDown = (event) => {
        let nextKey = 'ArrowRight', prevKey = 'ArrowLeft';
        if (isRTL) {
            // swap keys
            [nextKey, prevKey] = [prevKey, nextKey];
        }
        if (event.key === nextKey) {
            event.preventDefault();
            props.onFocusedIndexChange(Math.min(props.focusedIndex + 1, props.items.length - 1));
        }
        else if (event.key === prevKey) {
            event.preventDefault();
            props.onFocusedIndexChange(Math.max(props.focusedIndex - 1, 0));
        }
        else if ([' ', 'Enter'].includes(event.key)) {
            event.preventDefault();
            props.onChange(props.focusedIndex);
        }
    };
    const { isOverflowed, setContentRef, scrollTo } = useScrollableRef(props.direction);
    const buttons = buttonType === ButtonType.PROGRESSNUMBER ? (_jsxs("span", Object.assign({ style: { color: textColor } }, { children: [items.length ? props.selectedIndex + 1 : 0, " / ", items.length] }))) : (items.map((value, index) => (_jsx(StackButton, { index: index, dndPrefix: dndPrefix, selected: index === props.selectedIndex, onChange: props.onChange, reorderItem: props.onReorderItems, scrollTo: scrollTo, buttonType: buttonType, label: value, tabLocation: tabLocation, focused: index === props.focusedIndex, isFocusActive: isFocusActive, enableDrag: !!props.enableDrag }, index))));
    return (_jsxs("div", Object.assign({ ref: ref, style: outerDivStyle, "data-testid": props.id }, { children: [_jsx(Button, { icon: { icon: previousIcon, tooltip: '' }, variant: ButtonVariant.Light, disabled: !previousEnabled, onPress: () => previousEnabled && props.onChange(props.selectedIndex - 1) }), _jsx("div", Object.assign({ role: "tablist", onKeyDown: onKeyDown, ref: setContentRef, style: {
                    display: 'flex',
                    flexGrow: 0,
                    flexShrink: 1,
                    overflow: 'hidden',
                    flexDirection,
                    textOverflow: 'ellipsis',
                    textAlign,
                    alignItems: 'center',
                }, onFocus: () => {
                    setIsFocusActive(true);
                }, onBlur: () => {
                    setIsFocusActive(false);
                } }, { children: buttons })), _jsx(Button, { icon: { icon: nextIcon, tooltip: '' }, variant: ButtonVariant.Light, disabled: !nextEnabled, onPress: () => nextEnabled && props.onChange(props.selectedIndex + 1) }), !isOverflowed || props.items.length <= 1 ? null : (
            // MenuButton has a container div that interferes with the sizing of the inner button.
            // This div aligns the button with the nav buttons, but the overflow button will still
            // be slightly smaller than the nav buttons beside it.
            _jsx("div", Object.assign({ style: { display: 'flex', justifyContent: 'center', alignItems: 'center' } }, { children: _jsx(MenuButton, { variant: ButtonVariant.Icon, icon: { icon: TabOverflow }, hideDownArrow: true, sections: [
                        {
                            items: props.items.map((label, index) => ({
                                text: label,
                                checked: index === props.selectedIndex,
                            })),
                        },
                    ], onItemSelected: (location) => {
                        const itemIndex = location[0][1];
                        props.onChange(itemIndex);
                    } }) }))), _jsx(TabDragPreview, Object.assign({ dragType: dragType, dndPrefix: dndPrefix }, { children: (index) => {
                    const value = items[index];
                    return (_jsx(StackButton, { index: index, dndPrefix: dndPrefix, selected: index === props.selectedIndex, onChange: props.onChange, scrollTo: scrollTo, buttonType: buttonType, label: value, tabLocation: tabLocation, focused: index === props.focusedIndex, isFocusActive: isFocusActive, enableDrag: false }, index));
                } }))] })));
});
function useScrollableRef(direction) {
    const isHorizontal = direction === 'horizontal';
    const [isOverflowed, setIsOverflowed] = useState(false);
    const [setContentRef, getContentRef] = useResizeObserverRef(() => {
        checkForOverflow();
    });
    useEffect(() => {
        checkForOverflow();
    });
    const checkForOverflow = useCallback(() => {
        const content = getContentRef();
        if (!content)
            return;
        const { clientWidth, scrollWidth, clientHeight, scrollHeight } = content;
        setIsOverflowed(isHorizontal ? clientWidth < scrollWidth : clientHeight < scrollHeight);
    }, [getContentRef, isHorizontal]);
    const scrollFn = isHorizontal ? scrollHorizontal : scrollVertical;
    const scrollTo = useCallback((box) => {
        const content = getContentRef();
        if (!content)
            return;
        scrollFn(content, box);
    }, [getContentRef, scrollFn]);
    return {
        isOverflowed,
        setContentRef,
        scrollTo,
    };
}
